import type {
  NotificationItem,
  NotificationStatus,
} from "@components/NotificationsDropdown/NotificationsDropdown.types";
import {
  getPortServiceCategoryLabel,
  getPortServiceStatusInfo,
  STATUS_MAP as STATUS_MAP_PORT_SERVICES,
} from "@features/vessel-call/vessel-call-status/PortServices/preview/utils";
import type { PortService } from "@services/port-services/types";
import type { FromToLocation } from "@services/vessel-call/types";
import i18n from "i18next";
import { resolveTerminalName, resolveTerminalNamesInText } from "./terminalNameResolver";
import type { NotificationItemApi } from "./types";

const DOCUMENT_NAME_MAX_LENGTH = 32;

const STATUS_MAP: Record<string, NotificationStatus> = {
  "approved": "Approved",
  "rejected": "Rejected",
  "unsuspended": "Unsuspend",
  "suspended": "Suspend",
  "security_clearance_expired": "Expired",
  "security_clearance_about_to_expire": "Expiring soon",
  "delegated": "Delegated",
  "permanently rejected": "Permanently rejected",
  "pending": "Pending",
  "cancelled": "Cancelled",
  "restricted": "Restricted",
  "clear to operate": "Clear to operate",
  "updated": "Updated",
  "closed": "Closed",
  "overdue": "Overdue",
  "dispute": "Dispute",
  "pre-approved": "Verified",
  "rejected by marine department": "Rejected by Marine Department",
  "rejected by customs": "Rejected by Customs",
  "customs payment required": "Customs Payment Required",
  "autogenerated": "Autogenerated",
  "pending approval": "Pending",
  "submitted": "Submitted",
  "interrupted in-port": "Interrupted In-Port",
  "interrupted leave-port": "Interrupted Leave-Port",
  "expiring soon": "Expiring soon",
  "expired": "Expired",
  "expires today": "Expires today",
  "pending pre-approval": "Pending",
  "resubmit": "Rejected",
  "pending resubmission": "Rejected",
  "resolved": "Resolved",
  "awaiting approval": "Awaiting Approval",
  "pending resolution": "Pending Resolution",
  "update rejected": "Update Rejected",
  "update approved": "Update Approved",
};

const SECURITY_CLEARANCE_NOTIFICATION_STATUS_BY_TYPE: Partial<Record<string, NotificationStatus>> =
  {
    PERMANENTLY_REJECTED: "Permanently rejected",
    SUPERVISOR_REJECTED: "Rejected",
    OPERATOR_REJECTED: "Rejected",
    SPONSOR_COMPANY_REJECTED: "Rejected",
    SUPERVISOR_CANCELLED: "Cancelled",
    // Backend still sends CLOSED (this is named Cancelled in the UI)
    CLOSED: "Cancelled",
    OPERATOR_SUSPEND: "Suspend",
    SUPERVISOR_SUSPEND: "Suspend",
    OPERATOR_UNSUSPEND: "Unsuspend",
    SUPERVISOR_UNSUSPEND: "Unsuspend",
    SUPERVISOR_APPROVED: "Approved",
    SPONSOR_APPROVED: "Approved",
    OPERATOR_PENDING: "Pending",
    SPONSOR_COMPANY_PENDING: "Pending",
    SUPERVISOR_PENDING: "Pending",
    SECURITY_CLEARANCE_EXPIRED: "Expired",
    SECURITY_CLEARANCE_ABOUT_TO_EXPIRE: "Expiring soon",
    RENEWAL_ORIGINAL_CANCELLED: "Pending",
    RENEWAL_ORIGINAL_SUSPENDED: "Pending",
    RENEWAL_PREVIOUS_SUSPENDED: "Pending",
  };

const SECURITY_CLEARANCE_EXPIRY_TYPES = new Set([
  "SECURITY_CLEARANCE_EXPIRED",
  "SECURITY_CLEARANCE_ABOUT_TO_EXPIRE",
]);

const GATEPASS_EXCEPTION_STATUSES: readonly NotificationStatus[] = ["Rejected"];
const MOVEMENT_DEPARTURE_TERMINAL_APPROVED = "MOVEMENT_DEPARTURE_TERMINAL_APPROVED";
const MOVEMENT_REJECTED_BY_DEPARTURE_TERMINAL = "MOVEMENT_REJECTED_BY_DEPARTURE_TERMINAL";
const MOVEMENT_REINSTATEMENT_MARINE_DEPARTMENT_APPROVED =
  "MOVEMENT_REINSTATEMENT_MARINE_DEPARTMENT_APPROVED";
const VESSEL_CALL_CLOSED = "VESSEL_CALL_CLOSED";

const isGatepassExceptionStatus = (value: string): value is NotificationStatus =>
  GATEPASS_EXCEPTION_STATUSES.includes(value as NotificationStatus);

const normalizeNotificationCode = (value?: string | null) =>
  (value ?? "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
const isRejectedStatus = (value: string) => value.trim().toLowerCase() === "rejected";

const toStatus = (s: string): NotificationStatus => STATUS_MAP[s.trim().toLowerCase()] ?? "Unknown";

const statusMap: Partial<Record<NotificationStatus, string>> = {
  "Approved": "approved",
  "Rejected": "rejected",
  "Resubmit": "rejected",
  "Expired": "expired",
  "Expiring soon": "expiringSoon",
  "Expires today": "expiresToday",
  "Delegated": "delegated",
  "Unsuspend": "unsuspended",
  "Suspend": "suspended",
  "Permanently rejected": "permanentlyRejected",
  "Restricted": "restricted",
  "Clear to operate": "clearToOperate",
  "Cancelled": "cancelled",
  "Updated": "updated",
  "Closed": "closed",
  "Vessel Call Closed": "closed",
  "Overdue": "overdue",
  "Dispute": "dispute",
  "Verified": "preApproved",
  "Rejected by Marine Department": "rejectedByMarineDepartment",
  "Rejected by Customs": "rejectedByCustoms",
  "Customs Payment Required": "customsPaymentRequired",
  "Pending Approval": "pendingApproval",
  "Pending Pre-approval": "pendingPreApproval",
  "Resolved": "resolved",
  "Awaiting Approval": "awaitingApproval",
  "Pending Resolution": "pendingResolution",
  // DFT-0044 (FE-71/72): the 3 wireframe-evidenced Bay Hire chip labels (see
  // NotificationsDropdown.types.ts's own note on why only these 3, not all 12 real types).
  "Bay Hire License Renewal": "bayHireLicenseRenewal",
  "Bay Hire License Approved": "bayHireLicenseApproved",
  "Bay Hire License Expired": "bayHireLicenseExpired",
  // DFT-0019 (AD-10): new chip for the pre-expiry windows, now that BAY_HIRE_EXPIRY_REMINDER no
  // longer maps to the "Expired" chip text.
  "Bay Hire License Expiring": "bayHireLicenseExpiring",
  "Update Approved": "updateApproved",
  "Update Rejected": "updateRejected",
};

export const statusKeyOf = (s: NotificationStatus) => statusMap[s] ?? "unknown";

export type SecurityClearanceExpiryDocumentKind = "securityClearance" | "passport" | "visa";

export function resolveSecurityClearanceExpiryDocumentKind(
  jsonPayload?: string | null,
): SecurityClearanceExpiryDocumentKind {
  const note = (jsonPayload ?? "").toLowerCase();
  if (/\bpassport\b/.test(note)) return "passport";
  if (/\bvisa\b/.test(note)) return "visa";
  return "securityClearance";
}

export function resolveSecurityClearanceExpiryTitleKey(
  type: string,
  jsonPayload?: string | null,
): string {
  const documentKind = resolveSecurityClearanceExpiryDocumentKind(jsonPayload);
  const isAboutToExpire = type === "SECURITY_CLEARANCE_ABOUT_TO_EXPIRE";

  const keys: Record<SecurityClearanceExpiryDocumentKind, string> = {
    securityClearance: isAboutToExpire
      ? "notifications.title.securityClearanceAboutToExpire"
      : "notifications.title.securityClearanceExpired",
    passport: isAboutToExpire
      ? "notifications.title.passportAboutToExpire"
      : "notifications.title.passportExpired",
    visa: isAboutToExpire
      ? "notifications.title.visaAboutToExpire"
      : "notifications.title.visaExpired",
  };

  return keys[documentKind];
}

// The two Bay Hire expiry types show the backend's submittedBy ("SYSTEM") on every surface,
// regardless of a stored submittedByRole (historical rows still carry "shipping-agent").
const SYSTEM_ATTRIBUTED_BAY_HIRE_TYPES = new Set(["BAY_HIRE_EXPIRY_REMINDER", "BAY_HIRE_EXPIRED"]);
export const isSystemAttributedBayHireNotification = (type?: string): boolean =>
  !!type && SYSTEM_ATTRIBUTED_BAY_HIRE_TYPES.has(type);

const PORT_SERVICES_CANCEL_TITLE = "notifications.title.portServicesCancel";
const PORT_SERVICES_STATUS_CHANGED_TITLE = "notifications.title.portServicesStatusChange";
const PORT_SERVICES_CANCEL_VESSEL_CALL_TITLE = "notifications.title.portServicesCancelVesselCall";
const PORT_SERVICES_CREATE_TITLE = "notifications.title.portServicesCreate";
const PORT_SERVICES_MESSAGE_SENT_TITLE = "notifications.title.portServiceMessageSent";
const PORT_SERVICES_NOC_ISSUED_TITLE = "notifications.title.portServicesNocIssued";
const PORT_SERVICES_NOC_EXPIRING_TITLE = "notifications.title.portServicesNocExpiringIn1Hour";
const PORT_SERVICES_NOC_EXPIRED_TITLE = "notifications.title.portServicesNocExpired";
const PORT_SERVICES_NOC_TYPES = new Set([
  "PORT_SERVICES_NOC_ISSUED_HSE",
  "PORT_SERVICES_NOC_ISSUED_MARINE_OPERATOR",
  "PORT_SERVICES_NOC_ISSUED_SHIPPING_AGENT",
  "PORT_SERVICES_NOC_EXPIRING_MARINE_OPERATOR",
  "PORT_SERVICES_NOC_EXPIRING_SHIPPING_AGENT",
  "PORT_SERVICES_NOC_EXPIRED_MARINE_OPERATOR",
  "PORT_SERVICES_NOC_EXPIRED_SHIPPING_AGENT",
]);

const PORT_SERVICES_CANCEL_TYPES = {
  PORT_SERVICES_CANCEL_MARINE_OPERATOR: PORT_SERVICES_CANCEL_TITLE,
  PORT_SERVICES_CANCEL_SHIPPING_AGENT: PORT_SERVICES_CANCEL_TITLE,
  PORT_SERVICES_CANCEL_MARINE_SUPERVISOR: PORT_SERVICES_CANCEL_TITLE,
  PORT_SERVICES_CANCEL_DRY_BULK: PORT_SERVICES_CANCEL_TITLE,
  PORT_SERVICES_CANCEL_FOTT: PORT_SERVICES_CANCEL_TITLE,
  PORT_SERVICES_CANCEL_HARBOUR_MASTER: PORT_SERVICES_CANCEL_TITLE,
  PORT_SERVICES_CANCEL_HSE: PORT_SERVICES_CANCEL_TITLE,
  PORT_SERVICES_CANCEL_OPERATIONS: PORT_SERVICES_CANCEL_TITLE,
  PORT_SERVICES_CANCEL_VESSEL_CALL: PORT_SERVICES_CANCEL_VESSEL_CALL_TITLE,
  PORT_SERVICES_CANCEL_VESSEL_CALL_SHIPPING_AGENT: PORT_SERVICES_CANCEL_VESSEL_CALL_TITLE,
} as const;

const PORT_SERVICES_STATUS_CHANGED_TYPES = {
  PORT_SERVICES_STATUS_CHANGED_SHIPPING_AGENT: PORT_SERVICES_STATUS_CHANGED_TITLE,
  PORT_SERVICES_STATUS_CHANGED_FOTT: PORT_SERVICES_STATUS_CHANGED_TITLE,
  PORT_SERVICES_STATUS_CHANGED_OPERATIONS: PORT_SERVICES_STATUS_CHANGED_TITLE,
  PORT_SERVICES_STATUS_CHANGED_HSE: PORT_SERVICES_STATUS_CHANGED_TITLE,
  PORT_SERVICES_STATUS_CHANGED_MARINE_OPERATOR: PORT_SERVICES_STATUS_CHANGED_TITLE,
  PORT_SERVICES_STATUS_CHANGED_MARINE_SUPERVISOR: PORT_SERVICES_STATUS_CHANGED_TITLE,
  PORT_SERVICES_STATUS_CHANGED_MARINE_OFFICE: PORT_SERVICES_STATUS_CHANGED_TITLE,
  PORT_SERVICES_STATUS_CHANGED_HARBOUR_MASTER: PORT_SERVICES_STATUS_CHANGED_TITLE,
  PORT_SERVICES_NOC_ISSUED_MARINE_OPERATOR: PORT_SERVICES_NOC_ISSUED_TITLE,
  PORT_SERVICES_NOC_ISSUED_SHIPPING_AGENT: PORT_SERVICES_NOC_ISSUED_TITLE,
  PORT_SERVICES_NOC_ISSUED_HSE: PORT_SERVICES_NOC_ISSUED_TITLE,
  PORT_SERVICES_NOC_EXPIRING_MARINE_OPERATOR: PORT_SERVICES_NOC_EXPIRING_TITLE,
  PORT_SERVICES_NOC_EXPIRING_SHIPPING_AGENT: PORT_SERVICES_NOC_EXPIRING_TITLE,
  PORT_SERVICES_NOC_EXPIRED_MARINE_OPERATOR: PORT_SERVICES_NOC_EXPIRED_TITLE,
  PORT_SERVICES_NOC_EXPIRED_SHIPPING_AGENT: PORT_SERVICES_NOC_EXPIRED_TITLE,
} as const;

const PORT_SERVICES_CREATE_TYPES = {
  PORT_SERVICES_CREATE_SHIPPING_AGENT: PORT_SERVICES_CREATE_TITLE,
  PORT_SERVICES_CREATE_FOTT: PORT_SERVICES_CREATE_TITLE,
  PORT_SERVICES_CREATE_OPERATIONS: PORT_SERVICES_CREATE_TITLE,
  PORT_SERVICES_CREATE_HSE: PORT_SERVICES_CREATE_TITLE,
  PORT_SERVICES_CREATE_MARINE_OPERATOR: PORT_SERVICES_CREATE_TITLE,
  PORT_SERVICES_CREATE_MARINE_SUPERVISOR: PORT_SERVICES_CREATE_TITLE,
  PORT_SERVICES_CREATE_MARINE_OFFICE: PORT_SERVICES_CREATE_TITLE,
  PORT_SERVICES_CREATE_HARBOUR_MASTER: PORT_SERVICES_CREATE_TITLE,
} as const;

const PORT_SERVICES_MESSAGE_SENT_TYPES = {
  PORT_SERVICES_MESSAGE_SENT_SHIPPING_AGENT: PORT_SERVICES_MESSAGE_SENT_TITLE,
  PORT_SERVICES_MESSAGE_SENT_FOTT: PORT_SERVICES_MESSAGE_SENT_TITLE,
  PORT_SERVICES_MESSAGE_SENT_OPERATIONS: PORT_SERVICES_MESSAGE_SENT_TITLE,
  PORT_SERVICES_MESSAGE_SENT_HSE: PORT_SERVICES_MESSAGE_SENT_TITLE,
  PORT_SERVICES_MESSAGE_SENT_MARINE_OPERATOR: PORT_SERVICES_MESSAGE_SENT_TITLE,
  PORT_SERVICES_MESSAGE_SENT_MARINE_SUPERVISOR: PORT_SERVICES_MESSAGE_SENT_TITLE,
  PORT_SERVICES_MESSAGE_SENT_MARINE_OFFICE: PORT_SERVICES_MESSAGE_SENT_TITLE,
  PORT_SERVICES_MESSAGE_SENT_HARBOUR_MASTER: PORT_SERVICES_MESSAGE_SENT_TITLE,
} as const;

const BYPASS_NOTIFICATION_TYPES_WITH_DEDICATED_TITLES = [
  "BYPASS_CLOSED",
  "BYPASS_OVERDUE_FOTT",
  "BYPASS_OVERDUE_CUSTOMER",
  "BYPASS_OVERDUE_CUSTOMER_WEEKLY",
] as const;

const TITLE_KEY_BY_TYPE: Record<string, string> = {
  PERMANENTLY_REJECTED: "notifications.title.permanentlyRejected",
  SUPERVISOR_REJECTED: "notifications.title.supervisorRejected",
  OPERATOR_REJECTED: "notifications.title.supervisorRejected",
  SUPERVISOR_CANCELLED: "notifications.title.supervisorCancelled",
  OPERATOR_SUSPEND: "notifications.title.operatorSuspended",
  SUPERVISOR_SUSPEND: "notifications.title.operatorSuspended",
  OPERATOR_UNSUSPEND: "notifications.title.operatorUnsuspended",
  SUPERVISOR_UNSUSPEND: "notifications.title.operatorUnsuspended",
  SPONSOR_COMPANY_REJECTED: "notifications.title.supervisorRejected",
  SUPERVISOR_APPROVED: "notifications.title.supervisorApproved",
  SPONSOR_APPROVED: "notifications.title.sponsorApproved",
  OPERATOR_PENDING: "notifications.title.operatorPending",
  SPONSOR_COMPANY_PENDING: "notifications.title.sponsorPending",
  SUPERVISOR_PENDING: "notifications.title.supervisorPending",
  SHIPPING_AGENT_SUBMITTED: "notifications.title.shippingAgentSubmitted",
  SHIPPING_AGENT_SUBMITTED_BUNKER: "notifications.title.shippingAgentSubmittedBunker",
  SHIPPING_AGENT_CANCELLED_BUNKER: "notifications.title.shippingAgentCancelledBunker",
  SHIPPING_AGENT_UPDATED: "notifications.title.shippingAgentUpdated",
  MARINE_DEPARTMENT_SUBMITTED: "notifications.title.marineDepartmentSubmitted",
  MARINE_DEPARTMENT_UPDATED: "notifications.title.marineDepartmentUpdated",
  MARINE_DEPARTMENT_APPROVED: "notifications.title.marineDepartmentApproved",
  MARINE_DEPARTMENT_REJECTED: "notifications.title.marineDepartmentRejected",
  MARINE_OFFICE_UPDATED_BUNKER: "notifications.title.marineOfficeUpdatedBunker",
  RESTRICTION_ADDED: "notifications.title.restrictionAdded",
  RESTRICTION_REMOVED: "notifications.title.restrictionRemoved",
  SECURITY_CLEARANCE_EXPIRED: "notifications.title.expired",
  SECURITY_CLEARANCE_ABOUT_TO_EXPIRE: "notifications.title.aboutToExpire",
  RENEWAL_ORIGINAL_CANCELLED: "notifications.title.renewalOriginalCancelled",
  RENEWAL_ORIGINAL_SUSPENDED: "notifications.title.renewalOriginalSuspended",
  RENEWAL_PREVIOUS_SUSPENDED: "notifications.title.renewalPreviousSuspended",
  VESSEL_DOCUMENT_ABOUT_TO_EXPIRE: "notifications.title.vesselDocumentAboutToExpire",
  VESSEL_DOCUMENT_ABOUT_TO_EXPIRE_BUNKER: "notifications.title.vesselDocumentAboutToExpire",
  VESSEL_DOCUMENT_EXPIRED: "notifications.title.vesselDocumentExpired",
  VESSEL_DOCUMENT_EXPIRED_BUNKER: "notifications.title.vesselDocumentExpired",
  BUNKER_BARGE_CANCELLATION_REJECTED: "notifications.title.bunkerBargeCancelactionRejected",
  BUNKER_BARGE_CANCELLATION_APPROVED: "notifications.title.bunkerBargeCancelactionApproved",
  // Movement service notification types
  MOVEMENT_DEPARTURE_TERMINAL_APPROVED: "notifications.title.movementServiceApprovedByTerminal",
  MOVEMENT_ARRIVAL_TERMINAL_APPROVED: "notifications.title.movementServiceApproved",
  MOVEMENT_DEPARTURE_TERMINAL_PENDING_APPROVAL:
    "notifications.title.awaitingMarineDepartmentApproval",
  MOVEMENT_ARRIVAL_TERMINAL_PENDING_APPROVAL:
    "notifications.title.awaitingMarineDepartmentApproval",
  MOVEMENT_MARINE_DEPARTMENT_PENDING_APPROVAL:
    "notifications.title.awaitingMarineDepartmentApproval",
  MOVEMENT_MARINE_DEPARTMENT_APPROVED: "notifications.title.movementServiceApproved",
  MOVEMENT_CREATED_BY_MARINE_DEPARTMENT:
    "notifications.title.movementServiceCreatedByMarineDepartment",
  MOVEMENT_CREATED_AND_APPROVED_BY_MARINE_DEPARTMENT:
    "notifications.title.movementServiceCreatedApprovedByMarineDepartment",
  MOVEMENT_REJECTED_BY_MARINE_DEPARTMENT: "notifications.title.movementServiceRejected",
  MOVEMENT_REJECTED_BY_MARINE_DEPARTMENT_TO_ARRIVAL_TERMINAL:
    "notifications.title.movementServiceRejected",
  MOVEMENT_REJECTED_BY_MARINE_DEPARTMENT_TO_DEPARTURE_TERMINAL:
    "notifications.title.movementServiceRejected",
  MOVEMENT_REJECTED_BY_DEPARTURE_TERMINAL:
    "notifications.title.movementRejectedByDepartureTerminal",
  MOVEMENT_REJECTED_BY_ARRIVAL_TERMINAL: "notifications.title.movementRejectedByArrivalTerminal",
  MOVEMENT_CANCELED_BY_MARINE_DEPARTMENT_TO_TERMINAL_DEPARTURE:
    "notifications.title.movementServiceCancelled",
  MOVEMENT_CANCELED_BY_MARINE_DEPARTMENT_TO_TERMINAL_ARRIVAL:
    "notifications.title.movementServiceCancelled",
  MOVEMENT_CANCELED_BY_MARINE_DEPARTMENT_TO_SHIPPING_AGENT:
    "notifications.title.movementServiceCancelled",
  MOVEMENT_CANCELED_BY_SHIPPING_AGENT_TO_MARINE_DEPARTMENT:
    "notifications.title.movementServiceCancelled",
  MOVEMENT_CANCELED_BY_SHIPPING_AGENT_TO_TERMINAL_DEPARTURE:
    "notifications.title.movementServiceCancelled",
  MOVEMENT_CANCELED_BY_SHIPPING_AGENT_TO_TERMINAL_ARRIVAL:
    "notifications.title.movementServiceCancelled",
  MOVEMENT_UPDATED: "notifications.title.movementUpdated",
  MOVEMENT_STARTED: "notifications.title.movementStarted",
  MOVEMENT_CLOSED: "notifications.title.movementClosed",
  MOVEMENT_STARTED_AUTOMATICALLY: "notifications.title.movementAutogenerated",
  MOVEMENT_REINSTATEMENT_MARINE_DEPARTMENT_PENDING_APPROVAL:
    "notifications.title.movementReinstatingPending",
  MOVEMENT_REINSTATEMENT_MARINE_DEPARTMENT_APPROVED:
    "notifications.title.movementReinstatingApproved",
  MOVEMENT_CANCELED_BY_VESSEL_CALL_CANCELLATION:
    "notifications.title.movementServiceCancelledByVesselCallCancellation",

  // Vessel call notification types
  VESSEL_CALL_MARINE_SUPERVISOR_SUBMIT_PENDING_PRE_APPROVAL:
    "notifications.title.vesselCallMarineSupervisorSubmitPendingPreApproval",
  VESSEL_CALL_MARINE_OPERATOR_SUBMIT_PENDING_APPROVAL:
    "notifications.title.vesselCallMarineOperatorSubmitPendingApproval",
  VESSEL_CALL_ARRIVAL_UPDATED: "notifications.title.vesselCallArrivalUpdated",
  VESSEL_CALL_PRE_APPROVED: "notifications.title.vesselCallPreApproved",
  VESSEL_CALL_CLOSED: "notifications.title.vesselCallClosed",
  VESSEL_CALL_CANCELLED_BY_SHIPPING_AGENT: "notifications.title.vesselCallCancelledByShippingAgent",
  VESSEL_CALL_CANCELLED_BY_MARINE_DEPARTMENT:
    "notifications.title.vesselCallCancelledByMarineDepartment",
  VESSEL_CALL_CANCELLED_TO_SHIPPING_AGENT:
    "notifications.title.vesselCallCancelledByMarineDepartment",

  // Departure notification types
  DEPARTURE_PENDING_TERMINAL_APPROVAL: "notifications.title.departurePendingTerminalApproval",
  DEPARTURE_PENDING_CUSTOMS_PRE_APPROVAL: "notifications.title.departurePendingCustomsPreApproval",
  DEPARTURE_PENDING_MARINE_DEPARTMENT_APPROVAL:
    "notifications.title.departurePendingMarineDepartmentApproval",
  DEPARTURE_APPROVED: "notifications.title.departureApproved",
  DEPARTURE_REJECTED_BY_MARINE_DEPARTMENT:
    "notifications.title.departureRejectedByMarineDepartment",
  DEPARTURE_REJECTED_BY_CUSTOMS: "notifications.title.departureRejectedByCustoms",
  DEPARTURE_DISPUTED_BY_TERMINAL: "notifications.title.departureDisputedByTerminal",
  DEPARTURE_CUSTOMS_PAYMENT_REQUIRED: "notifications.title.departureCustomsPaymentRequired",
  INTERRUPTED_LEAVE_PORT: "notifications.title.vesselCallInterruptedLeavePort",
  INTERRUPTED_IN_PORT: "notifications.title.vesselCallInterruptedInPort",

  // Shipping agency transfer notification types
  SHIPPING_AGENCY_TRANSFER_PENDING_APPROVAL:
    "notifications.title.shippingAgencyTransferPendingApproval",
  SHIPPING_AGENCY_TRANSFER_PENDING_MARINE_DEPARTMENT_APPROVAL:
    "notifications.title.shippingAgencyTransferPendingMarineDepartmentApproval",
  SHIPPING_AGENCY_TRANSFER_APPROVED_BY_SHIPPING_AGENCY2:
    "notifications.title.shippingAgencyTransferApprovedByShippingAgency2",
  SHIPPING_AGENCY_TRANSFER_REJECTED_BY_SHIPPING_AGENCY2:
    "notifications.title.shippingAgencyTransferRejectedByShippingAgency2",
  SHIPPING_AGENCY_TRANSFER_APPROVED_BY_MARINE_DEPARTMENT:
    "notifications.title.shippingAgencyTransferApprovedByMarineDepartment",
  SHIPPING_AGENCY_TRANSFER_REJECTED_BY_MARINE_DEPARTMENT:
    "notifications.title.shippingAgencyTransferRejectedByMarineDepartment",
  SHIPPING_AGENCY_TRANSFER_CANCELLED_BY_VESSEL_CALL:
    "notifications.title.shippingAgencyTransferCancelledByVesselCall",
  SHIPPING_AGENCY_TRANSFER_CANCELLED_BY_VESSEL_CALL_SHIPPING_AGENT:
    "notifications.title.shippingAgencyTransferCancelledByVesselCall",

  // DEFICIENCY
  DEFICIENCY_AWAITING_APPROVAL: "notifications.title.deficiencyAwaitingApproval",
  DEFICIENCY_PENDING_RESOLUTION: "notifications.title.deficiencyPendingResolution",
  DEFICIENCY_RESOLVED: "notifications.title.deficiencyResolved",
  DEFICIENCY_REJECTED_RESOLUTION: "notifications.title.deficiencyRejectedResolution",

  // Terminal services
  COSTUMER_REQUESTED_TERMINAL_SERVICE: "notifications.title.customerRequestedTerminalService",
  FOTT_REJECTED_TERMINAL_SERVICE: "notifications.title.fottRejectedTerminalService",
  FOTT_APPROVED_TERMINAL_SERVICE: "notifications.title.fottApprovedTerminalService",
  FOTT_CANCELLED_TERMINAL_SERVICE: "notifications.title.fottCancelledTerminalService",
  BYPASS_CLOSED: "notifications.title.bypassClosed",
  BYPASS_OVERDUE_FOTT: "notifications.title.bypassOverdue",
  BYPASS_OVERDUE_CUSTOMER: "notifications.title.bypassOverdue",
  BYPASS_OVERDUE_CUSTOMER_WEEKLY: "notifications.title.bypassOverdueWeekly",

  // NOMINATION
  NOMINATION_AWAITING_APPROVAL: "notifications.title.nominationAwaitingApproval",
  NOMINATION_APPROVED: "notifications.title.nominationApproved",
  NOMINATION_REJECTED: "notifications.title.nominationRejected",
  NOMINATION_UPDATED: "notifications.title.nominationUpdated",

  // DRY BULK NOMINATION
  DRY_BULK_NOMINATION_SUBMITTED: "notifications.title.dryBulkNominationSubmitted",
  DRY_BULK_NOMINATION_RESUBMITTED: "notifications.title.dryBulkNominationResubmitted",
  DRY_BULK_NOMINATION_UPDATED: "notifications.title.dryBulkNominationUpdated",
  DRY_BULK_NOMINATION_CANCELLED: "notifications.title.dryBulkNominationCancelled",
  DRY_BULK_NOMINATION_APPROVED: "notifications.title.dryBulkNominationApproved",
  DRY_BULK_NOMINATION_REJECTED: "notifications.title.dryBulkNominationRejected",
  DRY_BULK_NOMINATION_RESOURCES_UPDATED: "notifications.title.dryBulkNominationResourcesUpdated",

  // PORT SERVICE
  ...PORT_SERVICES_CANCEL_TYPES,
  ...PORT_SERVICES_STATUS_CHANGED_TYPES,
  ...PORT_SERVICES_CREATE_TYPES,
  ...PORT_SERVICES_MESSAGE_SENT_TYPES,

  //DISPUTES
  NO_DISPUTE_SUBMITTED_SHIPPING_AGENT: "notifications.title.noDisputeSubmitted",
  DISPUTE_RAISED_SHIPPING_AGENT: "notifications.title.disputeRaised",
  DISPUTE_RAISED_MARINE_OPERATOR: "notifications.title.disputeRaised",
  DISPUTE_RAISED_MARINE_SUPERVISOR: "notifications.title.disputeRaised",
  DISPUTE_RESOLVED_SHIPPING_AGENT: "notifications.title.disputeResolved",
  DISPUTE_RESOLVED_MARINE_OPERATOR: "notifications.title.disputeResolved",
  DISPUTE_RESOLVED_MARINE_SUPERVISOR: "notifications.title.disputeResolved",
  DISPUTE_RESOLUTION_PENDING_TERMINAL: "notifications.title.disputeResolutionPending",
  DISPUTE_REQUIRED_FOR_DEPARTURE: "notifications.title.disputeRequiredForDeparture",

  // DFT-0044 (FE-71/72): all 12 real BAY_HIRE_* types (NotificationType.java) - purely additive,
  // same as every other feature's own block above. Only 3 of these are shown in either wireframe
  // drop (22_Notifications_dropdown.png/23_Notifications_view_all.png: "Bay Hire License Renewal"/
  // "Bay Hire License Approved"/"Bay Hire License Expired", spaced correctly per Q19 - the dropdown
  // mockup's own chip text is missing a space on the first two, a defect to flag rather than copy).
  // Mapped to BAY_HIRE_RENEWAL_SUBMITTED / BAY_HIRE_PAYMENT_APPROVED / BAY_HIRE_EXPIRY_REMINDER as
  // the closest-named real types - nothing states this mapping explicitly, so treat it as a
  // presumption alongside FE-38/FE-57/etc. The other 9 real types have no wireframe coverage at
  // all; their titles here are a direct, literal restatement of the domain event name they
  // correspond to (not an invented design), so they read as functional rather than as UI copy.
  BAY_HIRE_REQUESTED: "notifications.title.bayHireRequested",
  BAY_HIRE_PRE_APPROVED: "notifications.title.bayHirePreApproved",
  BAY_HIRE_PRE_APPROVAL_REJECTED: "notifications.title.bayHirePreApprovalRejected",
  BAY_HIRE_CANCELLED_BY_VENDOR: "notifications.title.bayHireCancelledByVendor",
  BAY_HIRE_CANCELLED_BY_OPS: "notifications.title.bayHireCancelledByOps",
  BAY_HIRE_PAYMENT_RECEIPT_UPLOADED: "notifications.title.bayHirePaymentReceiptUploaded",
  BAY_HIRE_PAYMENT_APPROVED: "notifications.title.bayHireLicenseApproved",
  BAY_HIRE_PAYMENT_REJECTED: "notifications.title.bayHirePaymentRejected",
  BAY_HIRE_WAITING_LIST_ADDED: "notifications.title.bayHireWaitingListAdded",
  BAY_HIRE_WAITING_LIST_PROMOTED: "notifications.title.bayHireWaitingListPromoted",
  BAY_HIRE_RENEWAL_SUBMITTED: "notifications.title.bayHireLicenseRenewal",
  // DFT-0019 (AD-10): the backend now splits at expiry - BAY_HIRE_EXPIRY_REMINDER (5 pre-expiry
  // windows) keeps the wireframe-evidenced "Expiring" chip; the new BAY_HIRE_EXPIRED type
  // (expiry day + the day after) takes over the title BAY_HIRE_EXPIRY_REMINDER used to have.
  BAY_HIRE_EXPIRY_REMINDER: "notifications.title.bayHireLicenseExpiring",
  BAY_HIRE_EXPIRED: "notifications.title.bayHireLicenseExpired",
  DISPUTE_MISSING_BLOCKING_DEPARTURE: "notifications.title.missingBlockingDeparture",
  DISPUTE_PENDING_BLOCKING_DEPARTURE: "notifications.title.pendingBlockingDeparture",
  DISPUTE_RAISED_BLOCKING_DEPARTURE: "notifications.title.raisedBlockingDeparture",
};

// Used instead of the plain Bay Hire title when the notification carries the request number;
// rows without one (historical, or a failed request lookup at send time) keep the plain title.
const BAY_HIRE_NUMBERED_TITLE_KEY_BY_TYPE: Record<string, string> = {
  BAY_HIRE_REQUESTED: "notifications.title.bayHireRequestedNumbered",
  BAY_HIRE_PRE_APPROVED: "notifications.title.bayHirePreApprovedNumbered",
  BAY_HIRE_PRE_APPROVAL_REJECTED: "notifications.title.bayHirePreApprovalRejectedNumbered",
  BAY_HIRE_CANCELLED_BY_VENDOR: "notifications.title.bayHireCancelledByVendorNumbered",
  BAY_HIRE_CANCELLED_BY_OPS: "notifications.title.bayHireCancelledByOpsNumbered",
  BAY_HIRE_PAYMENT_RECEIPT_UPLOADED: "notifications.title.bayHirePaymentReceiptUploadedNumbered",
  BAY_HIRE_PAYMENT_APPROVED: "notifications.title.bayHireLicenseApprovedNumbered",
  BAY_HIRE_PAYMENT_REJECTED: "notifications.title.bayHirePaymentRejectedNumbered",
  BAY_HIRE_WAITING_LIST_ADDED: "notifications.title.bayHireWaitingListAddedNumbered",
  BAY_HIRE_WAITING_LIST_PROMOTED: "notifications.title.bayHireWaitingListPromotedNumbered",
  BAY_HIRE_RENEWAL_SUBMITTED: "notifications.title.bayHireLicenseRenewalNumbered",
  BAY_HIRE_EXPIRY_REMINDER: "notifications.title.bayHireLicenseExpiringNumbered",
  BAY_HIRE_EXPIRED: "notifications.title.bayHireLicenseExpiredNumbered",
};

const fallbackTitle = (n: NotificationItemApi) => {
  return `${n.type
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())}${n.submittedFor ? ` for ${n.submittedFor}` : ""}`;
};

const parseSubmittedBy = (submittedBy: string | null | undefined) => {
  if (!submittedBy) return { name: "", roleFromName: "" };
  const parts = submittedBy
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length >= 2) {
    return { name: parts[0], roleFromName: parts.slice(1).join(", ") };
  }
  return { name: submittedBy, roleFromName: "" };
};

const formatPortServiceStatus = (status: string | null | undefined, serviceCategory?: string) => {
  if (!status) return "-";
  return STATUS_MAP_PORT_SERVICES[status]
    ? getPortServiceStatusInfo(status, serviceCategory).label
    : status;
};

const formatEnum = (value: string | null) =>
  value &&
  value
    .toLowerCase()
    .split("_")
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const parseSubmittedForOnVesselDocumentsExpiry = (
  submittedFor: string | null | undefined,
): { documentName: string; vesselName: string; days: string } => {
  if (!submittedFor) return { documentName: "", vesselName: "", days: "" };
  const parts = submittedFor
    .split("|")
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length === 2) {
    return { documentName: parts[0], vesselName: parts[1], days: "" };
  }
  if (parts.length === 3) {
    return { documentName: parts[0], vesselName: parts[1], days: parts[2] };
  }
  return { documentName: "", vesselName: "", days: "" };
};

// Parses movement service notification data from submittedFor or jsonPayload
// Note: locationCode can be a terminal, Sea, Anchorage, or any other location type

const parseMovementServiceData = (
  submittedFor: string | null | undefined,
  jsonPayload: string | null | undefined,
  locations: FromToLocation[],
): { vesselName: string; terminalName: string; terminalCode: string } => {
  // Try to extract vessel name and location from submittedFor or jsonPayload
  // Location can be a terminal, Sea, Anchorage etc.
  let vesselName = submittedFor || "";
  let locationCode = "";
  let locationName = "";

  // Check if submittedFor contains pipe-separated values
  if (submittedFor?.includes("|")) {
    const parts = submittedFor.split("|").map((s) => s.trim());
    vesselName = parts[0] || "";
    locationCode = parts[1] || "";
  }

  // Also check jsonPayload for location information
  if (jsonPayload) {
    const locationMatch = new RegExp(/\[([^\]]+)\]/).exec(jsonPayload);
    if (locationMatch && !locationCode) {
      locationCode = locationMatch[1];
    }
  }

  // Resolve location name if we have a code
  if (locationCode && locations.length > 0) {
    locationName = resolveTerminalName(locationCode, locations);
  }

  return { vesselName, terminalName: locationName, terminalCode: locationCode };
};

const translateDocumentName = (documentName: string | null | undefined) => {
  if (!documentName) return "";
  const translationKey = `notifications.documents.${documentName}`;
  const translatedDocumentName = i18n.t(translationKey, { defaultValue: documentName });

  return translatedDocumentName.length > DOCUMENT_NAME_MAX_LENGTH
    ? `${translatedDocumentName.slice(0, DOCUMENT_NAME_MAX_LENGTH - 3).trimEnd()}...`
    : translatedDocumentName;
};

const resolveVesselName = ({
  isMovementNotification,
  movementVesselName,
  submittedFor,
  vesselName,
}: {
  isMovementNotification: boolean;
  movementVesselName?: string | null;
  submittedFor?: string | null;
  vesselName?: string | null;
}): string => {
  if (isMovementNotification) {
    return movementVesselName?.trim() || submittedFor?.trim() || "—";
  }

  return vesselName?.trim() || "—";
};

const extractPortServicesJsonPayloadValue = (
  jsonPayload: string | null | undefined,
  key:
    | "previousStatus"
    | "status"
    | "serviceRequestNumber"
    | "nocId"
    | "serviceCategory"
    | "serviceType",
) => {
  if (!jsonPayload) return undefined;

  const match = new RegExp(new RegExp(`(?:^|;)${key}=([^;]*)`)).exec(jsonPayload);
  return match?.[1]?.trim() || undefined;
};

const resolveJsonPayloadForPortServiceNotifications = ({
  jsonPayload,
  isPortServicesNotification,
}: {
  jsonPayload: string | null | undefined;
  isPortServicesNotification: boolean;
}) => {
  if (!jsonPayload || !isPortServicesNotification) return null;

  const previousStatus = extractPortServicesJsonPayloadValue(jsonPayload, "previousStatus");
  const newStatus = extractPortServicesJsonPayloadValue(jsonPayload, "status");
  if (previousStatus && newStatus) return [previousStatus, newStatus];

  return jsonPayload
    .split(";", 1)[0]
    .split(",")
    .map((value) => value.trim());
};

export const mapApiToUi = (
  n: NotificationItemApi,
  terminalLocations: FromToLocation[] = [],
  entityData: PortService | null,
): NotificationItem => {
  let status: NotificationStatus;
  const isPortServicesRelatedEntity = n.relatedEntityType === "PORT_SERVICES";
  const isMovementNotification = n.relatedEntityType === "MOVEMENT";
  const isGatepassNotification = n.relatedEntityType === "GATEPASS";
  const isPortOperatorLicenseNotification = n.relatedEntityType === "PORT_OPERATOR_LICENSE";
  const isNonDisputedMovementNotification =
    isMovementNotification &&
    (normalizeNotificationCode(n.status) === "NON_DISPUTED" ||
      normalizeNotificationCode(n.type) === "MOVEMENT_NON_DISPUTED");
  const isDisputedMovementNotification =
    isMovementNotification &&
    (normalizeNotificationCode(n.status) === "DISPUTED" ||
      normalizeNotificationCode(n.type) === "MOVEMENT_DISPUTED");
  const isDisputeNotification = n.relatedEntityType === "DISPUTE";
  const isNominationNotification = n.relatedEntityType === "NOMINATION";
  const isDryBulkNominationNotification = n.relatedEntityType === "DRY_BULK_NOMINATION";
  const isTerminalServiceNotification = [
    "GANTRY_CRANE",
    "NITROGEN_SUPPLY",
    "PIGGING",
    "BYPASS",
  ].includes(n.relatedEntityType ?? "");

  // DFT-0044 (FE-71/72): the 3 Bay Hire types actually shown in either wireframe drop get their
  // own literal chip text, same technique as VESSEL_CALL_CLOSED below - direct from `n.type`, not
  // `n.status` (which the other 9 real BAY_HIRE_* types still fall through to, via the generic
  // `toStatus(n.status)` path at the bottom of this if-chain).
  if (n.type === "BAY_HIRE_RENEWAL_SUBMITTED") {
    status = "Bay Hire License Renewal";
  } else if (n.type === "BAY_HIRE_PAYMENT_APPROVED") {
    status = "Bay Hire License Approved";
  } else if (n.type === "BAY_HIRE_EXPIRY_REMINDER") {
    // DFT-0019 (AD-10): split at expiry. BAY_HIRE_EXPIRY_REMINDER now covers only the 5
    // pre-expiry windows (30/21/14/7/1 days), so it gets its own "Expiring" chip rather than
    // reusing "Expired" - see BAY_HIRE_EXPIRED below for the two windows that still mean expired.
    status = "Bay Hire License Expiring";
  } else if (n.type === "BAY_HIRE_EXPIRED") {
    status = "Bay Hire License Expired";
  } else if (n.type === VESSEL_CALL_CLOSED) {
    status = "Vessel Call Closed";
  } else if (n.type === MOVEMENT_DEPARTURE_TERMINAL_APPROVED || isNonDisputedMovementNotification) {
    status = "Approved";
  } else if (n.type === MOVEMENT_REJECTED_BY_DEPARTURE_TERMINAL || isDisputedMovementNotification) {
    status = "Rejected";
  } else if (
    n.relatedEntityType === "SECURITY_CLEARANCE" &&
    SECURITY_CLEARANCE_NOTIFICATION_STATUS_BY_TYPE[n.type]
  ) {
    status = SECURITY_CLEARANCE_NOTIFICATION_STATUS_BY_TYPE[n.type]!;
  } else if (
    n.relatedEntityType === "SECURITY_CLEARANCE" &&
    // Backend status remains CLOSED; this is named Cancelled in the UI
    normalizeNotificationCode(n.status) === "CLOSED"
  ) {
    status = "Cancelled";
  } else if (
    n.type === "SECURITY_CLEARANCE_EXPIRED" ||
    n.type === "SECURITY_CLEARANCE_ABOUT_TO_EXPIRE"
  ) {
    status = toStatus(n.type);
  } else if (isGatepassNotification) {
    status = isGatepassExceptionStatus(n.status) ? n.status : toStatus(n.status);
  } else if (isTerminalServiceNotification && isRejectedStatus(n.status)) {
    status = "Rejected";
  } else if (
    isTerminalServiceNotification &&
    n.status.trim().toLowerCase() === "pending approval"
  ) {
    status = "Pending Approval";
  } else if (
    (isNominationNotification || isDryBulkNominationNotification) &&
    n.status.trim().toLowerCase() === "pending approval"
  ) {
    status = "Pending Approval";
  } else {
    status = toStatus(n.status);
  }
  const statusKey = statusKeyOf(status);
  const { name, roleFromName } = parseSubmittedBy(n.submittedBy);
  const submittedByRole = (n.submittedByRole ?? roleFromName ?? "").trim() || null;
  const customerName = n.relatedCompanyName?.trim() || n.submittedBy || "—";
  const normalizedRole = (submittedByRole ?? "").toUpperCase().replace(/-/g, "_");
  const department =
    normalizedRole === "MARINE_OPERATOR"
      ? "Marine Operator"
      : normalizedRole === "MARINE_SUPERVISOR"
        ? "Marine Supervisor"
        : normalizedRole === "SHIPPING_AGENT"
          ? "Shipping Agent"
          : normalizedRole === "MARINE_OFFICE"
            ? "Marine Office"
            : normalizedRole === "TERMINAL"
              ? n.submittedBy
              : null;
  const notification: NotificationItemApi & { department: string | null } = { ...n, department };

  // Parse data based on notification type
  const { documentName, vesselName, days } = [
    "VESSEL_DOCUMENT_EXPIRED",
    "VESSEL_DOCUMENT_ABOUT_TO_EXPIRE",
    "VESSEL_DOCUMENT_EXPIRED_BUNKER",
    "VESSEL_DOCUMENT_ABOUT_TO_EXPIRE_BUNKER",
  ].includes(n.type)
    ? parseSubmittedForOnVesselDocumentsExpiry(n.submittedFor)
    : { documentName: "", vesselName: "", days: "" };

  // When notification is about vessel document expiry, use specific title key if expires today
  // Otherwise, determine title key based on type
  let titleKey =
    days === "0" ? "notifications.title.vesselDocumentExpiresToday" : TITLE_KEY_BY_TYPE[n.type];

  if (isTerminalServiceNotification) {
    titleKey = BYPASS_NOTIFICATION_TYPES_WITH_DEDICATED_TITLES.includes(
      n.type as (typeof BYPASS_NOTIFICATION_TYPES_WITH_DEDICATED_TITLES)[number],
    )
      ? TITLE_KEY_BY_TYPE[n.type]
      : status === "Rejected"
        ? TITLE_KEY_BY_TYPE.FOTT_REJECTED_TERMINAL_SERVICE
        : status === "Approved"
          ? TITLE_KEY_BY_TYPE.FOTT_APPROVED_TERMINAL_SERVICE
          : status === "Cancelled"
            ? TITLE_KEY_BY_TYPE.FOTT_CANCELLED_TERMINAL_SERVICE
            : TITLE_KEY_BY_TYPE.COSTUMER_REQUESTED_TERMINAL_SERVICE;
  }

  if (isMovementNotification) {
    const rules: Array<{ when: boolean; key: string }> = [
      {
        when: n.type === MOVEMENT_DEPARTURE_TERMINAL_APPROVED || isNonDisputedMovementNotification,
        key: "notifications.title.movementServiceApprovedByTerminal",
      },
      {
        when: n.type === "MOVEMENT_CREATED_BY_MARINE_DEPARTMENT",
        key: "notifications.title.movementServiceCreatedByMarineDepartment",
      },
      {
        when: n.type === "MOVEMENT_CREATED_AND_APPROVED_BY_MARINE_DEPARTMENT",
        key: "notifications.title.movementServiceCreatedApprovedByMarineDepartment",
      },
      {
        when: n.type === MOVEMENT_REINSTATEMENT_MARINE_DEPARTMENT_APPROVED,
        key: "notifications.title.movementReinstatingApproved",
      },
      {
        when: n.type === "MOVEMENT_CANCELED_BY_VESSEL_CALL_CANCELLATION",
        key: "notifications.title.movementServiceCancelledByVesselCallCancellation",
      },
      {
        when: n.type === MOVEMENT_REJECTED_BY_DEPARTURE_TERMINAL || isDisputedMovementNotification,
        key: "notifications.title.movementRejectedByDepartureTerminal",
      },
      { when: status === "Approved", key: "notifications.title.movementServiceApproved" },
      { when: status === "Cancelled", key: "notifications.title.movementServiceCancelled" },
      {
        when: status === "Pending" && n.type === "MOVEMENT_ARRIVAL_TERMINAL_APPROVED",
        key: "notifications.title.movementServiceApproved",
      },
      {
        when:
          status === "Pending" &&
          n.type === "MOVEMENT_REINSTATEMENT_MARINE_DEPARTMENT_PENDING_APPROVAL",
        key: "notifications.title.movementReinstatingPending",
      },
      {
        when: status === "Pending",
        key: "notifications.title.awaitingMarineDepartmentApproval",
      },
      {
        when: status === "Resubmit" && n.type === "MOVEMENT_REJECTED_BY_MARINE_DEPARTMENT",
        key: "notifications.title.movementServiceRejected",
      },
      {
        when: status === "Resubmit" && n.type === "MOVEMENT_REJECTED_BY_ARRIVAL_TERMINAL",
        key: "notifications.title.movementRejectedByArrivalTerminal",
      },
      {
        when: status === "Resubmit" && n.type === MOVEMENT_REJECTED_BY_DEPARTURE_TERMINAL,
        key: "notifications.title.movementRejectedByDepartureTerminal",
      },
      {
        when: status === "Updated" && n.type === "MOVEMENT_STARTED",
        key: "notifications.title.movementStarted",
      },
      {
        when: status === "Updated" && n.type === "MOVEMENT_UPDATED",
        key: "notifications.title.movementUpdated",
      },
      { when: status === "Closed", key: "notifications.title.movementClosed" },
      { when: status === "Autogenerated", key: "notifications.title.movementAutogenerated" },
    ];

    titleKey = rules.find((r) => r.when)?.key ?? titleKey;
  }

  if (isGatepassNotification) {
    const rules: Array<{ when: boolean; key: string }> = [
      { when: status === "Approved", key: "notifications.title.gatepassApproved" },
      {
        when: status === "Pending" && n.type === "GATEPASS_REQUESTED_TO_INDIVIDUAL",
        key: "notifications.title.gatepassPendingForIndividual",
      },
      {
        when: status === "Pending",
        key: "notifications.title.gatepassPending",
      },
      {
        when: status === "Rejected",
        key: "notifications.title.gatepassRejected",
      },
    ];

    titleKey = rules.find((r) => r.when)?.key ?? titleKey;
  }

  if (isNominationNotification) {
    const rules: Array<{ when: boolean; key: string }> = [
      {
        when: status === "Pending Approval" && n.type === "NOMINATION_RESUBMIT_AWAITING_APPROVAL",
        key: "notifications.title.nominationResubmittedAwaitingApproval",
      },
      {
        when: status === "Pending Approval",
        key: "notifications.title.nominationAwaitingApproval",
      },
      { when: status === "Approved", key: "notifications.title.nominationApproved" },
      { when: status === "Rejected", key: "notifications.title.nominationRejected" },
    ];

    titleKey = rules.find((r) => r.when)?.key ?? titleKey;
  }

  if (SECURITY_CLEARANCE_EXPIRY_TYPES.has(n.type)) {
    titleKey = resolveSecurityClearanceExpiryTitleKey(n.type, n.jsonPayload);
  }

  if (isPortOperatorLicenseNotification) {
    if (n.type === "AWAITING_MANAGING_DIRECTOR_APPROVAL") {
      titleKey = "notifications.title.portOperatorLicenseAwaitingManagingDirectorApproval";
    } else if (n.type === "AWAITING_MANAGING_DIRECTOR_OFFICE_VERIFICATION") {
      titleKey =
        "notifications.title.portOperatorLicenseAwaitingManagingDirectorOfficeVerification";
    }
  }

  const {
    vesselName: movementVesselName,
    terminalName,
    terminalCode,
  } = isMovementNotification
    ? parseMovementServiceData(n.submittedFor, n.jsonPayload, terminalLocations)
    : { vesselName: "", terminalName: "", terminalCode: "" };

  const statuses = resolveJsonPayloadForPortServiceNotifications({
    jsonPayload: n.jsonPayload,
    isPortServicesNotification: isPortServicesRelatedEntity,
  });
  const isPortServicesNocNotification =
    isPortServicesRelatedEntity && PORT_SERVICES_NOC_TYPES.has(n.type);
  const portServicesJsonPayloadCategory = isPortServicesRelatedEntity
    ? extractPortServicesJsonPayloadValue(n.jsonPayload, "serviceCategory")
    : undefined;
  const portServicesJsonPayloadType = isPortServicesRelatedEntity
    ? extractPortServicesJsonPayloadValue(n.jsonPayload, "serviceType")
    : undefined;
  const portServicesNocType = n.serviceType?.trim() || undefined;
  const portServicesType =
    (isPortServicesNocNotification ? portServicesNocType : portServicesJsonPayloadType) ??
    entityData?.serviceType ??
    "";
  const portServicesCategory =
    portServicesJsonPayloadCategory ?? entityData?.serviceCategory ?? "-";
  const portServicesCategoryLabel = getPortServiceCategoryLabel(portServicesCategory);

  let docType: string | undefined;
  if (n.jsonPayload?.includes("Security clearance")) {
    docType = n.jsonPayload?.split(" ").slice(0, 3).join(" ");
  } else {
    docType = n.jsonPayload?.split(" ").slice(0, 2).join(" ");
  }

  // Resolve location names in jsonPayload for movement notifications
  let resolvedJsonPayload = n.jsonPayload ?? null;
  if (isMovementNotification && resolvedJsonPayload && terminalLocations.length > 0) {
    resolvedJsonPayload = resolveTerminalNamesInText(resolvedJsonPayload, terminalLocations);
  }

  // Parse date and time from submittedAt for movement notifications
  let formattedDate = "";
  let formattedTime = "";
  if ((isMovementNotification && n.submittedAt) || (isGatepassNotification && n.submittedAt)) {
    try {
      const date = new Date(n.submittedAt);
      if (!isNaN(date.getTime())) {
        // Format date as DD/MM/YYYY
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        formattedDate = `${day}/${month}/${year}`;
        // Format time as HH:mm
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        formattedTime = `${hours}:${minutes}`;
      }
    } catch {
      formattedDate = "";
      formattedTime = "";
    }
  }

  const numberedTitleKey = BAY_HIRE_NUMBERED_TITLE_KEY_BY_TYPE[n.type];
  if (numberedTitleKey && n.refOrderNumber != null && n.refOrderNumber !== "") {
    titleKey = numberedTitleKey;
  }

  // Build title with appropriate parameters
  const title = titleKey
    ? i18n.t(titleKey, {
        docType: docType,
        submittedFor: n.submittedFor ?? "—",
        status: i18n.t(`notifications.status.${statusKey}`),
        restrictionName: i18n.t(`notifications.restrictions.${n.restrictionName ?? ""}`),
        documentName: translateDocumentName(documentName),
        submittedBy: n.submittedBy ?? "—",
        customerName,
        vesselName: resolveVesselName({
          isMovementNotification,
          movementVesselName,
          submittedFor: n.submittedFor,
          vesselName,
        }),
        days: days ?? "—",
        refOrderNumber: n.refOrderNumber ?? "—",
        terminalName: terminalName || terminalCode || n.terminalId || "—",
        vcn: n.vcn ?? "—",
        interruptionReason: n.jsonPayload ?? "—",
        department: notification.department ?? "—",
        movementId: n.refOrderNumber ?? "-",
        ...(isDisputeNotification
          ? { vesselIMO: n.jsonPayload || "-", disputeId: n.refOrderNumber || "-" }
          : {}),
        // For movement notifications, include date, time, and user info
        ...(isMovementNotification
          ? {
              date: formattedDate || "—",
              time: formattedTime || "—",
              submittedBy: name || "—",
              submittedByRole: submittedByRole || "—",
            }
          : {}),
        ...(isPortServicesRelatedEntity
          ? {
              category: portServicesCategoryLabel || "-",
              type: portServicesType ? `- ${portServicesType}` : "",
              subType: entityData?.serviceSubType ? `${entityData.serviceSubType}` : "",
              vcn: n.vcn ?? "-",
              currentStatus: formatPortServiceStatus(statuses?.[0], portServicesCategory) || "-",
              newStatus: formatPortServiceStatus(statuses?.[1], portServicesCategory) || "-",
              portServiceId: n.refOrderNumber || "-",
              nocId: isPortServicesNocNotification
                ? extractPortServicesJsonPayloadValue(n.jsonPayload, "nocId") || "-"
                : "-",
            }
          : {}),
      })
    : fallbackTitle(n);

  const cancelAttachement =
    n.documentName && n.documentUrl
      ? { documentName: n.documentName, documentUrl: n.documentUrl }
      : null;

  return {
    id: n.id,
    type: n.type,
    status,
    title,
    submittedBy: name,
    submittedByRole: isPortServicesRelatedEntity ? formatEnum(submittedByRole) : submittedByRole,
    submittedFor: n.submittedFor ?? null,
    submittedAt: n.submittedAt,
    relatedEntityId: n.relatedEntityId,
    relatedEntityType: n.relatedEntityType,
    secondRelatedEntityId: n.secondRelatedEntityId,
    externalNote: n.externalNote,
    jsonPayload: resolvedJsonPayload,
    date: n.submittedAt,
    internalNotes: n.internalNotes,
    internalAttachments: n.internalAttachments,
    externalAttachments: n.externalAttachments,
    cancelAttachement,
    seen: n.seen,
  };
};
